import TenantComponents from '@components';
import tenantTheme from '@theme';
import { Popover } from 'antd';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Card, Flex, Icon, Skeleton } from '../../common';
import { formatWholeNumber } from '../../../utility/utility';
import { getTimeDateString } from '../../../utility/date';
import {
  QUOTA_OTHERS_KEY,
  canManageAgencyQuota,
  getPackageDurationLabel,
  getPackageTimeline,
  getQuotaSegmentColors,
  getQuotaTileClickProps,
  isAgencyAgent,
  isQuotaAgencyView,
} from '../../../utility/quota';
import { useGetQuotaCreditsWidgetDataQuery } from '../../../apis/quotaCredits';
import AssignProductsDrawer from '../../assign-products/assign-products-drawer';
import QuotaProductsDrawer from './quota-products-drawer';
import { LeftToUseTile, LeftToUseTiles, PackageSummaryGrid, StackBar, YourPackageTile } from './styled';

// Packages & Products header: the current package on the left, what is left to use per product on the right.
// A product opens the Available Products drawer at that product.
const QuotaPackageSummary = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.app.loginUser.user);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const platform = user?.platforms?.[0];
  const drawerRef = useRef();
  const [focus, setFocus] = useState(null);
  const [assignVisible, setAssignVisible] = useState(false);
  const [boughtOpen, setBoughtOpen] = useState(false);
  const infoDrawerRef = useRef();
  const isAgent = isAgencyAgent(user);
  const canManage = canManageAgencyQuota(user);

  const { data, isLoading } = useGetQuotaCreditsWidgetDataQuery(
    { platform_id: [platform?.id] },
    { skip: !user?.id, refetchOnMountOrArgChange: true },
  );

  if (!isLoading && !data?.package) return null;

  const pkg = data?.package;
  const isAgencyView = isQuotaAgencyView(data);
  const { daysLeft, elapsed } = getPackageTimeline(pkg);
  const durationLabel = getPackageDurationLabel(pkg);
  const bought = (data?.products || []).filter((product) => product.boughtSeparately > 0);
  const others = data?.others || [];
  const othersTotal = others.reduce((acc, product) => acc + product.total, 0);

  const openDrawer = (slug) => {
    setFocus(slug ? { slug, at: Date.now() } : null);
    drawerRef.current?.openDrawer();
  };

  const tile = (key, label, icon, color, available, total, onClick) => (
    <LeftToUseTile
      key={key}
      data-others={key === QUOTA_OTHERS_KEY || undefined}
      {...getQuotaTileClickProps(onClick, label)}
    >
      <div className="ltu-name">
        <span className="ltu-icon">
          <Icon icon={icon} size="16px" color={color} />
        </span>
        <span>{label}</span>
      </div>
      <div className="ltu-amount">
        <b>{formatWholeNumber(available)}</b> {t('of {{total}}', { total: formatWholeNumber(total) })}
      </div>
      <StackBar $height={4}>
        <i style={{ flex: `${available || 0} 1 0`, background: color }} />
        <i style={{ flex: `${Math.max((total || 0) - (available || 0), 0)} 1 0` }} />
      </StackBar>
    </LeftToUseTile>
  );

  const boughtPopover = (
    <div style={{ width: 260 }}>
      <b>{t('Bought separately')}</b>
      {!!pkg?.end_date && (
        <div className="fz-12 mb-8" style={{ color: tenantTheme['text-color-secondary'] }}>
          {t('Expires with your package, {{date}}', { date: getTimeDateString(pkg.end_date, undefined, true) })}
        </div>
      )}
      <Flex vertical gap="8px">
        {bought.map((product) => (
          <Flex key={product.slug} align="center" justify="space-between" gap="8px">
            <Flex align="center" gap="6px">
              <Icon icon={product.icon} size="14px" color={product.iconColor} />
              {t(product.name)}
            </Flex>
            <span className="fz-12" style={{ color: tenantTheme['text-color-secondary'] }}>
              {t('{{count}} bought', { count: formatWholeNumber(product.boughtSeparately) })}
            </span>
          </Flex>
        ))}
      </Flex>
      <Button
        type="link"
        className="p-0 mt-8"
        style={{ height: 'auto' }}
        onClick={() => {
          setBoughtOpen(false);
          openDrawer();
        }}
      >
        {t('View in Available Products')}
      </Button>
    </div>
  );

  return (
    <Card style={{ border: 0 }}>
      {isLoading ? (
        <Skeleton type="paragraph" paragraph={{ rows: 2 }} />
      ) : (
        <PackageSummaryGrid $mobile={isMobile}>
          <YourPackageTile>
            <Flex align="center" gap="10px">
              <span className="pkg-badge">
                <Icon icon="BsBoxSeam" size="20px" color={tenantTheme['primary-color']} />
              </span>
              <div>
                <div className="pkg-label">{isAgent ? t("Your agency's package") : t('Your package')}</div>
                <div className="pkg-name">
                  {t(pkg?.name)}
                  {!!durationLabel && <small>{t(durationLabel)}</small>}
                </div>
              </div>
            </Flex>
            <div>
              <div className="pkg-dates">
                {daysLeft != null && (
                  <span>
                    <b>{formatWholeNumber(daysLeft)}</b> {t('days left')}
                  </span>
                )}
                {!!pkg?.end_date && (
                  <span>{t('Ends {{date}}', { date: getTimeDateString(pkg.end_date, undefined, true) })}</span>
                )}
              </div>
              {elapsed != null && (
                <StackBar $height={4}>
                  <i style={{ flex: `${elapsed} 1 0`, background: tenantTheme['primary-color'] }} />
                  <i style={{ flex: `${1 - elapsed} 1 0` }} />
                </StackBar>
              )}
            </div>
          </YourPackageTile>
          <Flex vertical gap="8px" style={{ minWidth: 0 }}>
            <Flex align="center" justify="space-between" gap="8px">
              <span className="fz-12" style={{ color: tenantTheme['text-color-secondary'] }}>
                {t('Left to use')}
              </span>
              {!isAgent && !!bought.length && (
                <Popover
                  content={boughtPopover}
                  trigger="click"
                  placement="bottomRight"
                  open={boughtOpen}
                  onOpenChange={setBoughtOpen}
                >
                  <Button type="default" shape="round" size="small" style={{ height: 26, fontSize: 12 }}>
                    {t('Bought separately')} · {t('{{count}} items', { count: bought.length })}
                    <Icon icon="FiChevronDown" size="14px" />
                  </Button>
                </Popover>
              )}
            </Flex>
            <LeftToUseTiles $mobile={isMobile}>
              {(data?.core || []).map((product) =>
                tile(
                  product.slug,
                  t(product.name),
                  product.icon,
                  getQuotaSegmentColors(product.iconColor).main,
                  product.available,
                  product.total,
                  () => openDrawer(product.slug),
                ),
              )}
              {!!others.length &&
                tile(
                  QUOTA_OTHERS_KEY,
                  t('Add-ons'),
                  'BsGrid',
                  tenantTheme['gray-dark-color'],
                  data?.othersTotals?.available,
                  othersTotal,
                  () => openDrawer(QUOTA_OTHERS_KEY),
                )}
            </LeftToUseTiles>
            <Flex justify="end">
              <Button
                type="link"
                className="p-0"
                style={{ height: 'auto', textDecoration: 'underline' }}
                onClick={() => infoDrawerRef.current?.openDrawer(true)}
              >
                {t('What are Products?')}
              </Button>
            </Flex>
          </Flex>
        </PackageSummaryGrid>
      )}
      <QuotaProductsDrawer
        ref={drawerRef}
        data={data}
        focus={focus}
        isAgencyView={isAgencyView}
        isAgent={isAgent}
        canManage={canManage}
        canBuy={false}
        onAssign={() => {
          drawerRef.current?.closeDrawer();
          setAssignVisible(true);
        }}
      />
      {canManage && <AssignProductsDrawer isVisible={assignVisible} setIsVisible={setAssignVisible} />}
      <TenantComponents.QuotaInfoDrawer ref={infoDrawerRef} />
    </Card>
  );
};

export default QuotaPackageSummary;
